/**
 * IP deterrence.
 *
 * Honest framing, kept in the code so nobody mistakes it for a guarantee:
 * anything the browser renders, a determined person can extract — devtools, the
 * network tab, a screenshot and OCR, or reading the screen and typing. These
 * handlers stop casual selection, copy, right-click-save and print. They do not
 * stop a scraper, and they cannot stop a browser AI agent, which reads the same
 * DOM a screen reader does.
 *
 * What actually protects the asset lives elsewhere:
 *   - option scores never leave the server (the widget never receives them)
 *   - questions arrive one at a time, bound to a rate-limited session
 *   - CSP frame-ancestors restricts who can embed this
 * See docs/IP_PROTECTION.md.
 */

const TOAST_MS = 2200;

function ensureToast(): HTMLElement {
  let toast = document.getElementById("guard-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "guard-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.innerHTML =
      "This assessment is <b>proprietary to Mastery Partners</b> and can't be copied.";
    document.body.appendChild(toast);
  }
  return toast;
}

export function installGuard(): void {
  let timer: number | null = null;

  const notice = () => {
    const toast = ensureToast();
    toast.classList.add("on");
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => toast.classList.remove("on"), TOAST_MS);
  };

  // Never interfere with the contact form — autofill and paste must work, and
  // a visitor's own email address isn't Mastery's IP.
  const inForm = (target: EventTarget | null): boolean =>
    target instanceof Element && !!target.closest("input, textarea, select");

  for (const type of ["copy", "cut", "contextmenu", "selectstart", "dragstart"] as const) {
    document.addEventListener(type, (e) => {
      if (inForm(e.target)) return;
      e.preventDefault();
      if (type === "copy" || type === "cut" || type === "contextmenu") notice();
    });
  }

  document.addEventListener("keydown", (e) => {
    if (inForm(e.target)) return;
    const key = e.key.toLowerCase();
    const mod = e.ctrlKey || e.metaKey;
    if (mod && ["c", "x", "a", "s", "p", "u"].includes(key)) {
      e.preventDefault();
      notice();
    }
  });

  // A copy that fires another way lands on an attribution line rather than
  // clean question text.
  document.addEventListener("copy", (e) => {
    if (inForm(e.target)) return;
    e.clipboardData?.setData(
      "text/plain",
      "© Mastery Partners — Transition Readiness Assessment. " +
        "This instrument is proprietary and may not be reproduced.",
    );
    e.preventDefault();
  });

  // eslint-disable-next-line no-console
  console.log(
    "%cMastery Partners — proprietary assessment",
    "font-weight:bold;font-size:13px;color:#CFCA3A;background:#000026;padding:4px 8px;border-radius:4px",
  );
}
